// Tenant localisation: language, text direction and currency. Pure (no React,
// no Supabase) so it is unit-testable and usable from non-React code like the
// PDF exporter. The config lives in tenant_settings under LOCALE_SETTING_KEY.
//
// Currency is a DISPLAY setting: stored amounts are not converted. Changing
// USD to AED relabels the same numbers, it does not apply an exchange rate.

export const LOCALE_SETTING_KEY = 'localization';

export const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English', rtl: false },
  { code: 'ar', label: 'Arabic', native: 'العربية', rtl: true },
  { code: 'fr', label: 'French', native: 'Français', rtl: false },
  { code: 'es', label: 'Spanish', native: 'Español', rtl: false },
  { code: 'de', label: 'German', native: 'Deutsch', rtl: false },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];
export type Direction = 'auto' | 'ltr' | 'rtl';

export const CURRENCIES = [
  { code: 'USD', label: 'US dollar' },
  { code: 'EUR', label: 'Euro' },
  { code: 'GBP', label: 'Pound sterling' },
  { code: 'AED', label: 'UAE dirham' },
  { code: 'SAR', label: 'Saudi riyal' },
  { code: 'QAR', label: 'Qatari riyal' },
  { code: 'KWD', label: 'Kuwaiti dinar' },
  { code: 'BHD', label: 'Bahraini dinar' },
  { code: 'OMR', label: 'Omani rial' },
  { code: 'EGP', label: 'Egyptian pound' },
  { code: 'TRY', label: 'Turkish lira' },
  { code: 'INR', label: 'Indian rupee' },
  { code: 'PKR', label: 'Pakistani rupee' },
  { code: 'CNY', label: 'Chinese yuan' },
  { code: 'JPY', label: 'Japanese yen' },
  { code: 'SGD', label: 'Singapore dollar' },
  { code: 'AUD', label: 'Australian dollar' },
  { code: 'CAD', label: 'Canadian dollar' },
  { code: 'CHF', label: 'Swiss franc' },
  { code: 'ZAR', label: 'South African rand' },
  { code: 'BRL', label: 'Brazilian real' },
  { code: 'MXN', label: 'Mexican peso' },
] as const;

export interface LocaleConfig {
  language: LanguageCode;
  /** 'auto' follows the language (Arabic is RTL, the rest LTR). */
  direction: Direction;
  /** ISO 4217 code from CURRENCIES. */
  currency: string;
}

export const DEFAULT_LOCALE_CONFIG: LocaleConfig = { language: 'en', direction: 'auto', currency: 'USD' };

const isLanguage = (v: unknown): v is LanguageCode => LANGUAGES.some((l) => l.code === v);
const isCurrency = (v: unknown): v is string => CURRENCIES.some((c) => c.code === v);

/** Accepts anything (a jsonb value, a localStorage string) and returns a valid config. */
export function parseLocaleConfig(raw: unknown): LocaleConfig {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    language: isLanguage(r.language) ? r.language : DEFAULT_LOCALE_CONFIG.language,
    direction: r.direction === 'ltr' || r.direction === 'rtl' || r.direction === 'auto' ? r.direction : DEFAULT_LOCALE_CONFIG.direction,
    currency: isCurrency(r.currency) ? r.currency : DEFAULT_LOCALE_CONFIG.currency,
  };
}

export function resolveDirection(cfg: LocaleConfig): 'ltr' | 'rtl' {
  if (cfg.direction !== 'auto') return cfg.direction;
  return LANGUAGES.find((l) => l.code === cfg.language)?.rtl ? 'rtl' : 'ltr';
}

/**
 * BCP 47 tag used for Intl. Arabic is pinned to Latin digits (-u-nu-latn):
 * amounts, dates and counts in a PMO tool are read against spreadsheets and
 * ERP exports that use 0-9, and mixed digit systems are a common source of
 * misread figures. Letters, month names and separators still follow Arabic.
 */
export function intlLocale(cfg: LocaleConfig): string {
  return cfg.language === 'ar' ? 'ar-u-nu-latn' : cfg.language;
}

export interface CurrencyOptions {
  /** 1.2M / 450K style for cards and chart axes. */
  compact?: boolean;
}

export function formatCurrency(cfg: LocaleConfig, amount: number, { compact = false }: CurrencyOptions = {}): string {
  if (!Number.isFinite(amount)) return '—';
  try {
    return new Intl.NumberFormat(intlLocale(cfg), {
      style: 'currency',
      currency: cfg.currency,
      currencyDisplay: 'narrowSymbol',
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? (Math.abs(amount) >= 1_000_000 ? 1 : 0) : 0,
    }).format(amount);
  } catch {
    return `${cfg.currency} ${Math.round(amount).toLocaleString()}`;
  }
}

export function formatNumber(cfg: LocaleConfig, n: number): string {
  return Number.isFinite(n) ? new Intl.NumberFormat(intlLocale(cfg)).format(n) : '—';
}

export function formatDate(cfg: LocaleConfig, value: Date | string | number, withTime = false): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(intlLocale(cfg), withTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { dateStyle: 'medium' }).format(d);
}

// "en" resolves to en-GB for day/month order ("Monday 5 October"), matching the product's existing English output.
const dateLocale = (cfg: LocaleConfig) => (cfg.language === 'en' ? 'en-GB' : intlLocale(cfg));

/** "3 days ago" / "قبل 3 أيام". Always numeric ("1 day ago", not "yesterday") so English reads as before. */
export function formatRelative(cfg: LocaleConfig, value: Date | string | number, now: Date = new Date()): string {
  const d = value instanceof Date ? value : new Date(value);
  const diffSec = Math.round((d.getTime() - now.getTime()) / 1000);
  if (Number.isNaN(diffSec)) return '';
  const abs = Math.abs(diffSec);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000], ['month', 2_592_000], ['day', 86_400], ['hour', 3_600], ['minute', 60],
  ];
  const rtf = new Intl.RelativeTimeFormat(dateLocale(cfg), { numeric: 'always' });
  for (const [unit, secs] of units) {
    if (abs >= secs) return rtf.format(Math.trunc(diffSec / secs), unit);
  }
  return rtf.format(Math.trunc(diffSec), 'second');
}

/** "Monday 5 October" in the tenant's language. */
export function formatLongDate(cfg: LocaleConfig, value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(dateLocale(cfg), { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
}

/** "5 Oct" in the tenant's language. */
export function formatShortDate(cfg: LocaleConfig, value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(dateLocale(cfg), { day: 'numeric', month: 'short' }).format(d);
}
